import { useEffect } from 'react';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';

import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';

import FormCancelButton from '../../../components/buttons/FormCancelButton';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import { DiffRow } from './types';

const confirmSaveSchema = yup.object({
  reasonCode: yup.string().required('Required *'),
  note: yup.string(),
});

const useStyles = makeStyles()((theme: Theme) => ({
  dialogContainer: {
    '& .MuiDialog-paper': {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      width: '100%',
      maxWidth: '680px',
      boxSizing: 'border-box',
      padding: '32px',
      border: `1px solid ${theme.palette.secondary.dark}`,
      borderRadius: '16px',
      backgroundColor: theme.palette.primary.light,
    },
  },
  dialogTitle: {
    padding: 0,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontSize: '28px',
    fontWeight: 600,
    lineHeight: '34px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: 0,
    overflowY: 'auto',
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'center',
    padding: 0,
  },
  modalIntroText: {
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 600,
  },
  modalSectionLabel: {
    marginBottom: '8px',
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  diffSection: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
  },
  diffRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.secondary.dark}`,

    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  diffFieldLabel: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  diffPrior: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontFamily: 'Inter',
    fontSize: '13px',
  },
  diffNext: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 500,
  },
  noChangesText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'italic',
    padding: '12px 16px',
  },
}));

interface ClientPayoutSaveConfirmDialogProps {
  open: boolean;
  saving: boolean;
  changedRows: DiffRow[];
  reasonCodes: string[];
  onConfirm: (reasonCode: string, note: string) => void;
  onClose: () => void;
}

const ClientPayoutSaveConfirmDialog = ({
  open,
  saving,
  changedRows,
  reasonCodes,
  onConfirm,
  onClose,
}: ClientPayoutSaveConfirmDialogProps) => {
  const styles = useStyles();

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(confirmSaveSchema) as never,
    defaultValues: { reasonCode: '', note: '' },
  });

  const {
    handleSubmit,
    reset: resetForm,
    formState: { isValid },
  } = formMethods;

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = handleSubmit((values) => {
    onConfirm(values.reasonCode, values.note ?? '');
  });

  const reasonCodeOptions = reasonCodes.map((code) => ({
    id: code,
    label: code.replace(/_/g, ' '),
  }));

  return (
    <Dialog
      className={styles.classes.dialogContainer}
      maxWidth={false}
      onClose={handleClose}
      open={open}
    >
      <IconButton
        aria-label="close"
        disabled={saving}
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 24,
          top: 24,
          color: theme.palette.common.white,
        })}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle className={styles.classes.dialogTitle}>Confirm Change</DialogTitle>

      <FormProvider {...formMethods}>
        <DialogContent className={styles.classes.dialogContent}>
          <Typography className={styles.classes.modalIntroText}>
            Review your changes. Add an optional note for the audit trail.
          </Typography>

          <Box>
            <Typography className={styles.classes.modalSectionLabel}>Changes</Typography>
            <Box className={styles.classes.diffSection}>
              {changedRows.length === 0 ? (
                <Typography className={styles.classes.noChangesText}>
                  No changes detected.
                </Typography>
              ) : (
                changedRows.map((row) => (
                  <Box key={row.label} className={styles.classes.diffRow}>
                    <Typography className={styles.classes.diffFieldLabel}>{row.label}</Typography>
                    <Typography className={styles.classes.diffPrior}>Prior: {row.prior}</Typography>
                    <Typography className={styles.classes.diffNext}>New: {row.next}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>

          <Box>
            <Typography className={styles.classes.modalSectionLabel}>Reason Code *</Typography>
            <FormSelectField
              name="reasonCode"
              label="Select reason"
              options={reasonCodeOptions}
              width="100%"
            />
          </Box>

          <Box>
            <Typography className={styles.classes.modalSectionLabel}>Note (optional)</Typography>
            <FormTextField name="note" label="Note" isMultiline maxRows={4} />
          </Box>
        </DialogContent>

        <DialogActions className={styles.classes.dialogActions}>
          <Box display="flex" flexDirection="column" alignItems="center" gap="12px" width="320px">
            <Box width="100%">
              <FormSubmitButton
                buttonIcon={<SaveIcon />}
                buttonText={saving ? 'Saving...' : 'Confirm & Save'}
                disabled={saving || !isValid}
                onClick={handleConfirm}
              />
            </Box>
            <Box width="100%">
              <FormCancelButton disabled={saving} onClick={handleClose} />
            </Box>
          </Box>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default ClientPayoutSaveConfirmDialog;